import {
  Case,
  CaseDeleteReason,
  CasesService,
  OriginalCaseOption,
} from '@/@types/generated'
import Textarea from '@/components/Atoms/Textarea'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { useError } from '@/hooks/useError'
import format from '@/lib/format'
import { buildPath } from '@/routes'
import { zodResolver } from '@hookform/resolvers/zod'
import { InfoIcon, TriangleAlertIcon } from 'lucide-react'
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'
import { OriginalCaseField } from './OriginalCaseField'

interface DeleteCaseDialogProps {
  case: Case
  onDelete: () => void
}

type FormValues = {
  deleteReasonId: number
  notes?: string
}

// Takes a ref rather than the reasons array directly: react-hook-form captures the resolver
// passed to useForm() once and doesn't pick up a new one on every render, so building the
// schema fresh each render (closing over the latest `reasons`) would silently validate against
// a stale, empty list once the async reasons fetch resolves. Reading through a ref that's
// updated every render keeps the same resolver instance valid for the component's lifetime.
function buildSchema(reasonsRef: { current: CaseDeleteReason[] }) {
  return z
    .object({
      deleteReasonId: z.number({ message: 'Required' }),
      notes: z.string().max(500, 'Must be 500 characters or less').optional(),
    })
    .superRefine((data, ctx) => {
      const reason = reasonsRef.current.find(
        (r) => r.id === data.deleteReasonId
      )
      if (reason?.requiresNotes && !data.notes?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Required when the reason is Other',
          path: ['notes'],
        })
      }
    })
}

export default function DeleteCaseDialog({
  case: _case,
  onDelete,
  children,
}: PropsWithChildren<DeleteCaseDialogProps>) {
  const navigate = useNavigate()
  const { showSuccess } = useError()

  const [open, setOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [reasons, setReasons] = useState<CaseDeleteReason[]>([])
  const [originalCase, setOriginalCase] = useState<OriginalCaseOption | null>(
    null
  )
  const [originalCaseError, setOriginalCaseError] = useState<string | null>(
    null
  )

  const reasonsRef = useRef<CaseDeleteReason[]>([])
  reasonsRef.current = reasons

  const schema = useMemo(() => buildSchema(reasonsRef), [])
  const form = useForm<FormValues>({ resolver: zodResolver(schema) })

  useEffect(() => {
    if (!open) return
    CasesService.listCaseDeleteReasons().then((res) =>
      setReasons(res.deleteReasons ?? [])
    )
  }, [open])

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      form.reset()
      setOriginalCase(null)
      setOriginalCaseError(null)
      setSubmitError(false)
    }
  }

  const deleteReasonId = form.watch('deleteReasonId')
  const reason = reasons.find((r) => r.id === deleteReasonId)

  // Switching away from a reason that required an original must clear the pick - a stale
  // original must never be saved against a non-Duplicate reason.
  useEffect(() => {
    if (!reason?.requiresOriginal) {
      setOriginalCase(null)
      setOriginalCaseError(null)
    }
  }, [reason?.requiresOriginal])

  const appliedAmount = _case.penalty?.appliedAmount ?? 0
  const outstandingAmount = _case.penalty?.outstandingAmount ?? 0

  const handleSubmit = async (data: FormValues) => {
    if (appliedAmount > 0) return

    if (reason?.requiresOriginal && !originalCase) {
      setOriginalCaseError('Required')
      return
    }

    setSubmitError(false)
    setDeleting(true)
    try {
      await CasesService.deleteCase(_case.id!, {
        deleteReasonId: data.deleteReasonId,
        originalCaseId: originalCase?.id ?? null,
        notes: data.notes?.trim() || null,
      })

      const message =
        reason?.requiresOriginal && originalCase
          ? `${_case.code} was deleted as a duplicate of ${originalCase.code}.`
          : `${_case.code} was deleted (${reason?.name}).`

      showSuccess(message, 'Case deleted', {
        duration: 6_000,
        action: {
          label: 'Back to Cases',
          onClick: () => navigate(buildPath('/cases')),
        },
      })

      onDelete?.()
      setOpen(false)
    } catch {
      setSubmitError(true)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="flex max-h-[85vh] flex-col overflow-y-auto sm:max-w-2xl"
        variant="destructive"
      >
        <DialogHeader>
          <DialogTitle>Delete Case</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this case? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            noValidate
            className="flex flex-col gap-5"
          >
            <div className="flex flex-col gap-1">
              <Label className="text-base">Case #</Label>
              <span className="font-mono text-sm tracking-wide">
                {_case.code}
              </span>
            </div>

            <FormField
              control={form.control}
              name="deleteReasonId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delete reason</FormLabel>
                  <Select
                    value={field.value ? String(field.value) : undefined}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a reason">
                          {reason?.name ?? ''}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {reasons.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                          <div className="flex flex-col items-start">
                            <span>{r.name}</span>
                            {r.description && (
                              <span className="text-muted-foreground text-xs">
                                {r.description}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {reason?.requiresOriginal && (
              <div className="flex flex-col gap-1">
                <Label>Original case</Label>
                <OriginalCaseField
                  caseId={_case.id!}
                  value={originalCase}
                  onChange={(v) => {
                    setOriginalCase(v)
                    setOriginalCaseError(null)
                  }}
                />
                {originalCaseError && (
                  <p
                    role="alert"
                    className="text-destructive text-sm font-medium"
                  >
                    {originalCaseError}
                  </p>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <Textarea
                  label={`Notes${reason?.requiresNotes ? '' : ' (optional)'}`}
                  required={reason?.requiresNotes}
                  rows={3}
                  maxLength={500}
                  showCharacterCount
                  placeholder={
                    reason?.requiresNotes
                      ? 'Explain why this case is being deleted'
                      : 'Add context for the case history'
                  }
                  {...field}
                />
              )}
            />

            {appliedAmount > 0 && (
              <Alert variant="destructive">
                <TriangleAlertIcon />
                <AlertTitle>Can&apos;t delete this case</AlertTitle>
                <AlertDescription>
                  This case has {format.Currency(appliedAmount)} in payments
                  applied. Resolve the payment(s) on this case before it can be
                  deleted.
                </AlertDescription>
              </Alert>
            )}

            {outstandingAmount > 0 && (
              <Alert variant="info">
                <InfoIcon />
                <AlertTitle>Unpaid penalty will be deleted</AlertTitle>
                <AlertDescription>
                  This case has {format.Currency(outstandingAmount)} unpaid.
                  Deleting the case also deletes this penalty. The unpaid
                  balance won't be carried over to another case or stay
                  collectible.
                </AlertDescription>
              </Alert>
            )}

            {submitError && (
              <Alert variant="destructive" role="alert">
                <AlertTitle>The case wasn't deleted</AlertTitle>
                <AlertDescription>
                  Something went wrong on our end and nothing was changed. Try
                  again, and contact the helpdesk if it keeps happening.
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter className="flex-col-reverse sm:flex-row">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={deleting}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                variant="destructive"
                disabled={deleting || appliedAmount > 0}
                aria-busy={deleting}
                className="w-full sm:w-auto"
              >
                {deleting && <Spinner />}
                {deleting ? 'Deleting…' : 'Delete Case'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
